import React, { useEffect, useMemo, useState } from 'react';
import { Star, Check, EyeOff, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  hidden: 'bg-gray-100 text-gray-700 border-gray-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function AdminReviews() {
  const [status, setStatus] = useState('all');
  const [data, setData] = useState(null);

  const load = () => {
    const params = { limit: 100 };
    if (status !== 'all') params.status = status;
    api.get('/admin/reviews', { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const moderate = async (id, newStatus) => {
    try { await api.put(`/admin/reviews/${id}/moderate`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
    catch { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const counts = useMemo(() => {
    const items = data?.items || [];
    const c = { all: items.length, pending: 0, approved: 0, hidden: 0, rejected: 0 };
    items.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [data]);

  return (
    <div>
      <h1 className="text-2xl font-semibold">Reviews</h1>
      <p className="text-sm text-muted-foreground">Moderate product reviews before they appear on the storefront.</p>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-reviews-tabs">
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)} className={`rounded-full px-4 py-1.5 text-xs font-semibold ${status === t.key ? 'bg-[hsl(var(--primary))] text-white' : 'bg-white text-foreground'} border border-border`}>
            {t.label}{t.key !== 'all' && counts[t.key] ? ` · ${counts[t.key]}` : ''}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-reviews-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Product</th><th className="p-3">Rating</th><th className="p-3">Review</th><th className="p-3">Reviewer</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {data?.items?.map((r) => (
              <tr key={r.id} className="border-t border-border align-top">
                <td className="p-3"><Link to={`/product/${r.product_slug}`} className="inline-flex items-center gap-1 font-medium">{r.product_name || r.product_slug} <ExternalLink size={11} /></Link></td>
                <td className="p-3"><span className="inline-flex items-center gap-1"><Star size={13} className="fill-[hsl(var(--gaze-gold))] text-[hsl(var(--gaze-gold))]" /> {r.rating}</span></td>
                <td className="max-w-md p-3"><p className="font-medium">{r.title || '—'}</p><p className="line-clamp-3 text-xs text-muted-foreground">{r.comment}</p></td>
                <td className="p-3 text-xs">{r.name}{r.verified && <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] text-emerald-700">verified</span>}<br /><span className="text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span></td>
                <td className="p-3"><span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLES[r.status] || 'bg-gray-50'}`}>{r.status || 'approved'}</span></td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {r.status !== 'approved' && <button onClick={() => moderate(r.id, 'approved')} title="Approve" data-testid="review-approve" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white"><Check size={12} /> Approve</button>}
                    {r.status !== 'hidden' && <button onClick={() => moderate(r.id, 'hidden')} title="Hide" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1 text-[11px] font-semibold"><EyeOff size={12} /> Hide</button>}
                    <button onClick={() => del(r.id)} title="Delete" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1 text-[11px] font-semibold text-destructive"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No reviews in this view.</p>}
      </div>
    </div>
  );
}
